/* eslint-disable react-hooks/exhaustive-deps */
import { SelectOption, WrappedFormSingleSelectField } from 'components/form/FormSingleSelectField';
import FormTextField from 'components/form/FormTextField';
import { useField, useFormikContext } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import instance from '../../../../api';
import { endpoints } from '../../../../endpoints.config';
import { Toast, ToastMessageReason } from '../../../../helpers/toast';
import { useAfterMountEffect } from '../../../../helpers/useAfterMountEffect';
import { selectVerificationDetailsV3 } from '../../../../reducers/verification';
import Button from '../../../button/Button';
import { FormDateField } from '../../../form/FormDateField';
import { FormPhoneNumberField } from '../../../form/FormPhoneNumberField';
import { RadioButtons } from '../../../radiobuttons/radiobuttons';
import { FormValues, OwnerTreeStructure } from '../../schema';
import { formatValues } from '../helpers';
import { getDirectOwnersPercentageTotal, getParentTreeFieldname } from './helpers';
import UploadField from '../UploadField';
import { useVerificationContext } from '../verificationContext';

export type IndividualOwnerProps = {
    fieldName: string;
    treeFieldname: string;
    availableCountries: (SelectOption & { states: SelectOption[] | null })[];
    onClose: () => void;
};

export const IndividualOwner: React.FC<IndividualOwnerProps> = ({
    fieldName,
    treeFieldname,
    availableCountries,
    onClose,
}) => {
    const { rejectedDocuments } = useSelector(selectVerificationDetailsV3); //additionalData to have the stored form data for current stage?

    const { values, setFieldValue, setFieldError, setFieldTouched, validateForm } = useFormikContext<FormValues>();
    const { selectOptions } = useVerificationContext();

    const getDocumentId = (internalName: string): number | undefined => {
        const match = selectOptions?.openedDocumentsList?.find(
            (doc) => doc.internalName === internalName
        );
        return match?.id;
    };

    const individualIdDocumentId = getDocumentId('IBOIdDocument');
    const individualProofOfAddressId = getDocumentId('IBOProofOfAddress');
    const [{ value: countryValue }] = useField(`${fieldName}.country`);
    const [{ value }, , { setValue }] = useField(fieldName);
    const [{ value: treeValue }, , { setValue: setTreeValue }] = useField(treeFieldname);
    const parentTreeFieldname = getParentTreeFieldname(treeFieldname);
    const [{ value: parentTreeValue }] = useField<OwnerTreeStructure | OwnerTreeStructure[]>(
        parentTreeFieldname
    );

    // Store initial state in case they want to close the form rather than save
    const [initialValues] = useState({ individual: value, treeValue });
    // Set to true only on a successful Save; anything else (X, Escape, Close) is a discard.
    const savedRef = useRef(false);

    // Revert to the pre-edit values on any close that isn't a successful save, so filling the
    // form and closing it (e.g. via the modal's X) doesn't leave the owner looking "complete"
    // and flip its button from "Owner Data Required" to "Edit Owners Data".
    useEffect(() => {
        return () => {
            if (!savedRef.current) {
                setValue(initialValues.individual);
                setTreeValue(initialValues.treeValue);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const siblings = Array.isArray(parentTreeValue)
        ? parentTreeValue
        : parentTreeValue?.children ?? [];
    const isControllingParty = treeValue?.bControllingParty === true;
    const siblingTotal = getDirectOwnersPercentageTotal(siblings);
    // If this field's own value already exceeds 100, that's already flagged by its own
    // max(100) validation — don't also show the redundant aggregate-total message.
    const ownValueExceeds100 = (treeValue?.percentageOwned ?? 0) > 100;
    // Only controlling parties are allowed 0% — regular beneficial owners must be > 0%.
    const ownValueIsZeroOrLess =
        !isControllingParty &&
        treeValue?.percentageOwned != null &&
        treeValue.percentageOwned <= 0;
    const exceedsTotalPercentage = siblingTotal > 100 && !ownValueExceeds100;
    const exceedsTotalPercentageMessage =
        'The combined ownership percentage of beneficial owners cannot exceed 100%';

    const handleCloseWithoutSave = () => {
        setValue(initialValues.individual);
        setTreeValue(initialValues.treeValue);
        onClose();
    };

    // On (re)open, clear stale "touched" state only for fields that are genuinely
    // empty, so fields that were filled then discarded on a previous open don't
    // immediately show required errors before the user interacts with them again.
    // Fields that already have a value stay touched so real validation errors
    // (e.g. a duplicate email, a rejected document) remain visible instead of
    // being silently hidden by reopening the form.
    useEffect(() => {
        Object.keys(value ?? {}).forEach((key) => {
            const fieldValue = (value as any)[key];
            const isEmpty = fieldValue === undefined || fieldValue === null || fieldValue === '';
            setFieldTouched(`${fieldName}.${key}`, !isEmpty, false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (
            rejectedDocuments &&
            rejectedDocuments.hasOwnProperty('proofOfAddress') &&
            rejectedDocuments.proofOfAddress &&
            rejectedDocuments.proofOfAddress.length > 0
        ) {
            if (rejectedDocuments.proofOfAddress?.includes(value.proofOfAddressFilename)) {
                setFieldError(
                    `${fieldName}.proofOfAddressFilename`,
                    `File named ${value.proofOfAddressFilename} has been rejected, please upload another file`
                );
            }
        }
    }, []);

    const saveForm = async (): Promise<boolean> => {
        try {
            await instance.post(endpoints.businessVerificationV3Module.saveFormData, {
                ...formatValues(values),
                businessVerificationStep: values.businessVerificationStep,
            });
            return true;
        } catch (error: any) {
            Toast.openToastMessage(
                'Error saving form, please try again.',
                ToastMessageReason.ERROR
            );
            return false;
        }
    };

    const hasRequiredDocuments =
        !!(value?.idDocumentFilename?.trim?.() && value?.proofOfAddressFilename?.trim?.());

    const hasRequiredFields = !!(
        value?.firstName?.trim?.() &&
        value?.lastName?.trim?.() &&
        value?.title?.trim?.() &&
        value?.dateOfBirth != null &&
        value?.dateOfBirth !== '' &&
        value?.nationality?.trim?.() &&
        value?.email?.trim?.() &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value?.email ?? '') &&
        value?.phoneNumber?.trim?.() &&
        value?.country?.trim?.() &&
        value?.ssn?.trim?.() &&
        value?.stateProvince?.trim?.() &&
        value?.city?.trim?.() &&
        value?.addressLine1?.trim?.() &&
        value?.postcode?.trim?.() &&
        treeValue?.positionAtCompany?.trim?.() &&
        treeValue?.bControllingParty !== undefined &&
        treeValue?.bControllingParty !== null
    );

    const handleSave = async () => {
        const success = await saveForm();
        if (success) onClose();
    };

    const currentCountry = availableCountries.find((country) => country.value === countryValue);
    const stateOptions = currentCountry ? currentCountry.states : null;

    // Reset field when the country changes
    useAfterMountEffect(() => {
        setFieldValue(`${fieldName}.stateProvince`, '');
    }, [countryValue]);

    return (
        <div className="Form">
            <h1>Add Beneficial Owner Information</h1>
            <div className="Row">
                <FormTextField
                    label="First name*"
                    field={`${fieldName}.firstName`}
                    maxLength={200}
                />
                <FormTextField label="Last name*" field={`${fieldName}.lastName`} maxLength={200} />
            </div>
            <div className="Row">
                <FormTextField label="Title*" field={`${fieldName}.title`} maxLength={200} />
                <div style={{ width: '100%' }}>
                    <FormDateField
                        field={`${fieldName}.dateOfBirth`}
                        label="Date of Birth*"
                        error={true}
                        formSideBySide={false}
                        datePickerProps={{
                            maxDate: new Date(),
                        }}
                    />
                </div>
            </div>
            <div className="Row">
                <WrappedFormSingleSelectField
                    fieldName={`${fieldName}.nationality`}
                    options={availableCountries}
                    label="Nationality*"
                />
                <WrappedFormSingleSelectField
                    fieldName={`${fieldName}.occupationTypesId`}
                    options={selectOptions?.occupations ?? []}
                    label="Occupation"
                />
            </div>
            <div className="Row">
                <FormTextField label="Email*" field={`${fieldName}.email`} maxLength={200} />
            </div>
            <div className="Row">
                <FormPhoneNumberField
                    field={`${fieldName}.phoneNumber`}
                    label="Phone Number*"
                    sideBySide={true}
                />
                <WrappedFormSingleSelectField
                    fieldName={`${fieldName}.country`}
                    options={availableCountries}
                    label="Country*"
                />
            </div>
            <div className="Row">
                <FormTextField
                    field={`${fieldName}.ssn`}
                    label={'Tax ID number / SSN*'}
                    maxLength={200}
                />
                {stateOptions && stateOptions.length > 0 ? (
                    <div className="FormBuilderComponent SideBySide">
                        <WrappedFormSingleSelectField
                            fieldName={`${fieldName}.stateProvince`}
                            label={'State / Province*'}
                            options={stateOptions}
                        />
                    </div>
                ) : (
                    <FormTextField
                        field={`${fieldName}.stateProvince`}
                        label={'State / Province*'}
                        maxLength={200}
                    />
                )}
            </div>
            <div className="Row">
                <FormTextField field={`${fieldName}.city`} label={'City*'} maxLength={200} />
                <FormTextField
                    field={`${fieldName}.addressLine1`}
                    label={'Street Address*'}
                    maxLength={200}
                />
            </div>
            <div className="Row">
                <FormTextField
                    field={`${fieldName}.addressLine2`}
                    label={'Street Address 2'}
                    maxLength={200}
                />
                <FormTextField
                    field={`${fieldName}.postcode`}
                    label={'Postal / Zip Code*'}
                    maxLength={200}
                />
            </div>
            <div className="Row">
                <div className="FloatingErrorField">
                    <FormTextField
                        field={`${treeFieldname}.percentageOwned`}
                        label={'Percentage of Shares Owned (%)*'}
                        type="number"
                        min={0}
                        max={100}
                        maxLength={200}
                        hideError
                        className={
                            exceedsTotalPercentage || ownValueExceeds100 || ownValueIsZeroOrLess
                                ? 'ErrorInput'
                                : ''
                        }
                    />
                    {ownValueExceeds100 && (
                        <div className="ErrorLabel">
                            Percentage of shares owned cannot exceed 100%
                        </div>
                    )}
                    {ownValueIsZeroOrLess && !ownValueExceeds100 && (
                        <div className="ErrorLabel">
                            Percentage of shares owned must be greater than 0%
                        </div>
                    )}
                    {!ownValueExceeds100 && !ownValueIsZeroOrLess && exceedsTotalPercentage && (
                        <div className="ErrorLabel">{exceedsTotalPercentageMessage}</div>
                    )}
                </div>
                <FormTextField
                    field={`${treeFieldname}.positionAtCompany`}
                    label={'Position at the Company*'}
                    maxLength={200}
                />
            </div>
            <RadioButtons
                options={['Yes', 'No']}
                label={'Controlling Party*'}
                booleanField={true}
                fieldname={`${treeFieldname}.bControllingParty`}
                error={true}
            />
            <div className="FormSideBySide">
                <div className="FormBox">
                    <div className="FormLabel">
                        <label>
                            ID Document
                            <span>*</span>
                        </label>
                    </div>
                    <UploadField
                        fieldName={`${fieldName}.idDocumentFilename`}
                        displayError={true}
                        jsonField={true}
                        documentId={individualIdDocumentId}
                        onChange={saveForm}
                    />
                </div>
                <div className="FormBox">
                    <div className="FormLabel">
                        <label>
                            Proof of Address
                            <span>*</span>
                        </label>
                    </div>
                    <UploadField
                        fieldName={`${fieldName}.proofOfAddressFilename`}
                        displayError={true}
                        jsonField={true}
                        documentId={individualProofOfAddressId}
                        onChange={saveForm}
                    />
                </div>
            </div>
            <div className="IndividualOwnerButtons">
                <Button onClick={handleCloseWithoutSave} type="button" priority="secondary">
                    Close
                </Button>
                <Button
                    onClick={async () => {
                        const saved = await saveForm();
                        if (saved) {
                            savedRef.current = true;
                            onClose();
                        }
                    }}
                    type="button"
                >
                    Save
                </Button>
                
            </div>
        </div>
    );
};
